// Annunci dei clienti (immobili e lavoro): visibili a tutti; chi ha punti pubblica da solo, dopo il controllo
// automatico. Nell'app non si parla mai di soldi per il servizio: solo "punti" (la ricarica si fa allo studio).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Home, Briefcase, ChevronLeft, ChevronRight, Phone, MessageCircle, Copy, Flag, Plus, Loader2, Pause, Play, Trash2,
  Pencil, X, Camera, Info, Search, MapPin,
} from 'lucide-react';
import { tr, tz } from '../../pages/areaClientiLingua';
import { BASE, chiama, chiamaModulo, immagineConToken } from '../../pages/areaClientiApi';

const NOMI = {
  affitto: 'Affitto', vendita: 'Vendita', offro: 'Offro lavoro', cerco: 'Cerco lavoro',
  stanza: 'Stanza', posto_letto: 'Posto letto', appartamento: 'Appartamento', casa: 'Casa',
  confezione: 'Confezione / tessile', ristorazione: 'Ristorazione', pulizie: 'Pulizie', edilizia: 'Edilizia',
  magazzino: 'Magazzino / logistica', colf_badante: 'Colf / badante', negozio: 'Negozio / vendita', altro: 'Altro',
  tempo_pieno: 'Tempo pieno', part_time: 'Part-time', stagionale: 'Stagionale',
};
const n = (k) => tr(NOMI[k] || k || '');
const TIPI = { immobili: ['affitto', 'vendita'], lavoro: ['offro', 'cerco'] };
const IMMOBILI = ['stanza', 'posto_letto', 'appartamento', 'casa'];
const SETTORI = ['confezione', 'ristorazione', 'pulizie', 'edilizia', 'magazzino', 'colf_badante', 'negozio', 'altro'];
const ORARI = ['tempo_pieno', 'part_time', 'stagionale'];
const MOTIVI = [['truffa', 'Truffa o richiesta di soldi'], ['discriminazione', 'Discriminazione'], ['offensivo', 'Contenuto offensivo'],
  ['non_disponibile', 'Non più disponibile'], ['altro', 'Altro']];
const STATI = {
  in_verifica: ['In verifica…', 'bg-amber-100 text-amber-800'], online: ['Online', 'bg-green-100 text-green-800'],
  bloccato: ['Non pubblicato', 'bg-red-100 text-red-700'], da_guardare: ['In controllo dallo studio', 'bg-sky-100 text-sky-800'],
  pausa: ['In pausa', 'bg-slate-200 text-slate-700'], pausa_punti: ['In pausa: punti finiti', 'bg-amber-100 text-amber-800'],
  nascosto: ['Segnalato: in controllo', 'bg-sky-100 text-sky-800'], tolto: ['Tolto dallo studio', 'bg-red-100 text-red-700'],
};
const CHIAVE_NASCOSTI = 'jv_annunci_inserzionisti_nascosti';
const leggiNascosti = () => { try { return JSON.parse(localStorage.getItem(CHIAVE_NASCOSTI) || '[]'); } catch (_) { return []; } };
const nascondiInserzionista = (id) => { try { localStorage.setItem(CHIAVE_NASCOSTI, JSON.stringify([...new Set([...leggiNascosti(), id])])); } catch (_) { /* privato */ } };
const AVVISO = 'Annuncio pubblicato da un privato. JV Service non è intermediario e non è parte di alcun accordo tra le parti.';
const euro = (v) => `€ ${Number(v).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`;
const prezzo = (a) => (a.prezzo == null ? '' : `${euro(a.prezzo)}${a.tipo === 'affitto' ? tr('/mese') : ''}`);
const dataIt = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '');
const cifre = (s) => String(s || '').replace(/[^\d+]/g, '');
const internazionale = (s) => { const c = cifre(s).replace(/^\+/, '').replace(/^00/, ''); return c.length <= 10 ? `39${c}` : c; };

// foto ridotta nel telefono (lato max 1280 px, JPEG 0,8): 8 foto restano sotto il limite d'invio del server
async function riduciFoto(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((ok, ko) => { const i = new Image(); i.onload = () => ok(i); i.onerror = ko; i.src = url; });
    const s = Math.min(1, 1280 / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement('canvas');
    c.width = Math.round(img.naturalWidth * s); c.height = Math.round(img.naturalHeight * s);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    const blob = await new Promise((ok) => c.toBlob(ok, 'image/jpeg', 0.8));
    return new File([blob], 'foto.jpg', { type: 'image/jpeg' });
  } finally { URL.revokeObjectURL(url); }
}

function Scheda({ attiva, onClick, children }) {
  return (
    <button onClick={onClick} className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-[15px] font-semibold ${attiva ? 'bg-green-800 text-white shadow' : 'bg-white text-slate-600'}`}>
      {children}
    </button>
  );
}

function Pillola({ attiva, onClick, children }) {
  return (
    <button onClick={onClick} className={`shrink-0 rounded-full px-3 py-1.5 text-[13px] font-medium border ${attiva ? 'bg-green-50 border-green-700 text-green-800' : 'bg-white border-slate-200 text-slate-600'}`}>
      {children}
    </button>
  );
}

function Foto({ src, token, className }) {
  const [url, setUrl] = useState(token ? '' : `${BASE}${src}`);
  useEffect(() => {
    if (!token) { setUrl(`${BASE}${src}`); return undefined; }
    let vivo = true; let locale = '';
    immagineConToken(src, token).then((u) => { locale = u; if (vivo) setUrl(u); }).catch(() => {});
    return () => { vivo = false; if (locale) URL.revokeObjectURL(locale); };
  }, [src, token]);
  return url ? <img src={url} alt="" className={className} loading="lazy" /> : <div className={`${className} bg-slate-100`} />;
}

function Carta({ a, onApri, token, stato, piatta }) {
  return (
    <button onClick={onApri} className={`w-full text-left bg-white overflow-hidden flex ${piatta ? '' : 'rounded-2xl shadow-sm'}`}>
      {a.categoria === 'immobili' && (a.foto?.[0]
        ? <Foto src={a.foto[0]} token={token} className="w-28 h-28 object-cover shrink-0" />
        : <div className="w-28 h-28 bg-green-50 flex items-center justify-center shrink-0"><Home className="w-8 h-8 text-green-700" /></div>)}
      <div className="p-3 min-w-0 flex-1">
        <div className="text-[12px] text-green-800 font-semibold">{n(a.tipo)} · {n(a.immobile || a.settore)}</div>
        <div className="text-[16px] font-semibold text-slate-900 truncate">{a.titolo}</div>
        {a.categoria === 'immobili'
          ? <div className="text-[16px] font-bold text-slate-900">{prezzo(a)}</div>
          : <div className="text-[14px] text-slate-600">{n(a.orario)}{a.retribuzione ? ` · ${a.retribuzione}` : ''}</div>}
        <div className="text-[13px] text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{a.comune}{a.zona ? ` · ${a.zona}` : ''}</div>
        {stato && <span className={`inline-block mt-1 text-[12px] font-semibold rounded-full px-2 py-0.5 ${STATI[a.stato]?.[1]}`}>{tr(STATI[a.stato]?.[0] || a.stato)}</span>}
      </div>
    </button>
  );
}

function Elenco({ categoria, onApri }) {
  const [filtri, setFiltri] = useState({ tipo: '', immobile: '', settore: '', comune: '', prezzo_max: '' });
  const [comuni, setComuni] = useState([]);
  const [annunci, setAnnunci] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [altri, setAltri] = useState(false);
  const [errore, setErrore] = useState('');

  useEffect(() => { setFiltri({ tipo: '', immobile: '', settore: '', comune: '', prezzo_max: '' }); }, [categoria]);
  useEffect(() => { chiama(`/annunci/comuni?categoria=${categoria}`).then((r) => setComuni(r.comuni)).catch(() => setComuni([])); }, [categoria]);
  const carica = useCallback(async (p) => {
    const q = new URLSearchParams({ categoria, pagina: String(p) });
    Object.entries(filtri).forEach(([k, v]) => { if (v) q.set(k, v); });
    try {
      const r = await chiama(`/annunci?${q}`);
      setAnnunci((x) => (p === 1 ? r.annunci : [...(x || []), ...r.annunci])); setAltri(r.altri); setPagina(p); setErrore('');
    } catch (e) { setErrore(e.message); setAnnunci((x) => x || []); }
  }, [categoria, filtri]);
  useEffect(() => { setAnnunci(null); carica(1); }, [carica]);
  const f = (k) => (v) => setFiltri((x) => ({ ...x, [k]: x[k] === v ? '' : v }));

  return (
    <div className="space-y-3">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {TIPI[categoria].map((t) => <Pillola key={t} attiva={filtri.tipo === t} onClick={() => f('tipo')(t)}>{n(t)}</Pillola>)}
        {(categoria === 'immobili' ? IMMOBILI : SETTORI).map((t) => (
          <Pillola key={t} attiva={filtri[categoria === 'immobili' ? 'immobile' : 'settore'] === t}
            onClick={() => f(categoria === 'immobili' ? 'immobile' : 'settore')(t)}>{n(t)}</Pillola>
        ))}
      </div>
      <div className="flex gap-2">
        <select value={filtri.comune} onChange={(e) => setFiltri((x) => ({ ...x, comune: e.target.value }))}
          className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[15px]">
          <option value="">{tr('Tutti i comuni')}</option>
          {comuni.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {categoria === 'immobili' && (
          <input inputMode="numeric" placeholder={tr('Prezzo max €')} value={filtri.prezzo_max}
            onChange={(e) => setFiltri((x) => ({ ...x, prezzo_max: e.target.value.replace(/\D/g, '') }))}
            className="w-32 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[15px]" />
        )}
      </div>
      {errore && <div className="text-red-700 text-[14px]">{errore}</div>}
      {annunci === null && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-green-800" /></div>}
      {annunci && !annunci.length && (
        <div className="bg-white rounded-2xl p-6 text-center text-slate-500 text-[15px]">
          <Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />{tr('Nessun annuncio per ora')}
        </div>
      )}
      {(annunci || []).filter((a) => !leggiNascosti().includes(a.inserzionista)).map((a) => <Carta key={a.id} a={a} onApri={() => onApri(a.id)} />)}
      {altri && <button onClick={() => carica(pagina + 1)} className="w-full py-2.5 rounded-xl bg-white text-green-800 font-semibold">{tr('Mostra altri')}</button>}
    </div>
  );
}

function Galleria({ foto, token }) {
  const [i, setI] = useState(0);
  if (!foto?.length) return null;
  return (
    <div className="relative bg-black rounded-2xl overflow-hidden">
      <Foto src={foto[i]} token={token} className="w-full h-64 object-contain" />
      {foto.length > 1 && (
        <>
          <button onClick={() => setI((i - 1 + foto.length) % foto.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => setI((i + 1) % foto.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 flex items-center justify-center"><ChevronRight className="w-5 h-5" /></button>
          <div className="absolute bottom-2 inset-x-0 text-center text-white text-[12px]">{i + 1} / {foto.length}</div>
        </>
      )}
    </div>
  );
}

function Segnala({ id, token, onChiudi }) {
  const [motivo, setMotivo] = useState('');
  const [testo, setTesto] = useState('');
  const [stato, setStato] = useState('');
  const invia = async () => {
    setStato('invio');
    try { await chiama(`/annunci/${id}/segnala`, { metodo: 'POST', corpo: { motivo, testo }, token }); setStato('fatto'); } catch (e) { setStato(e.message); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center" onClick={onChiudi}>
      <div className="bg-white w-full max-w-xl rounded-t-3xl sm:rounded-3xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><div className="text-[18px] font-bold">{tr('Segnala annuncio')}</div><button onClick={onChiudi}><X className="w-5 h-5" /></button></div>
        {stato === 'fatto' ? <p className="text-[15px] text-green-800">{tr('Grazie: lo studio controllerà l\'annuncio.')}</p> : (
          <>
            {MOTIVI.map(([k, t]) => (
              <label key={k} className="flex items-center gap-2 text-[15px]"><input type="radio" checked={motivo === k} onChange={() => setMotivo(k)} />{tr(t)}</label>
            ))}
            <textarea value={testo} onChange={(e) => setTesto(e.target.value)} rows={3} maxLength={1000} placeholder={tr('Spiega cosa non va (facoltativo)')}
              className="w-full rounded-xl border border-slate-200 p-3 text-[15px]" />
            {stato && stato !== 'invio' && <div className="text-red-700 text-[14px]">{stato}</div>}
            <button disabled={!motivo || stato === 'invio'} onClick={invia} className="w-full rounded-xl bg-red-600 disabled:opacity-50 text-white font-semibold py-3">{tr('Invia segnalazione')}</button>
          </>
        )}
      </div>
    </div>
  );
}

function Contatti({ c }) {
  const [copiato, setCopiato] = useState(false);
  const copia = async () => { try { await navigator.clipboard.writeText(c.wechat); setCopiato(true); } catch (_) { setCopiato(true); } };
  return (
    <div className="grid gap-2">
      {c.telefono && <a href={`tel:${cifre(c.telefono)}`} className="flex items-center justify-center gap-2 rounded-xl bg-green-800 text-white font-semibold py-3"><Phone className="w-5 h-5" />{tr('Chiama')} {c.telefono}</a>}
      {c.whatsapp && <a href={`https://wa.me/${internazionale(c.whatsapp)}`} className="flex items-center justify-center gap-2 rounded-xl bg-emerald-500 text-white font-semibold py-3"><MessageCircle className="w-5 h-5" />WhatsApp</a>}
      {c.wechat && (
        <button onClick={copia} className="flex items-center justify-center gap-2 rounded-xl bg-white border border-slate-200 font-semibold py-3">
          <Copy className="w-5 h-5" />WeChat: <span className="select-all">{c.wechat}</span>{copiato && <span className="text-green-700 text-[13px]">· {tr('copiato')}</span>}
        </button>
      )}
    </div>
  );
}

function Dettaglio({ id, token, onIndietro }) {
  const [a, setA] = useState(null);
  const [errore, setErrore] = useState('');
  const [segnala, setSegnala] = useState(false);
  useEffect(() => { chiama(`/annunci/${id}`).then(setA).catch((e) => setErrore(e.message)); }, [id]);
  return (
    <div className="space-y-3">
      <button onClick={onIndietro} className="inline-flex items-center gap-1 rounded-full bg-white shadow px-3 py-1.5 text-[15px] text-green-800 font-medium"><ChevronLeft className="w-4 h-4" />{tr('Annunci')}</button>
      {errore && <div className="bg-white rounded-2xl p-5 text-slate-600">{errore}</div>}
      {!a && !errore && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-green-800" /></div>}
      {a && (
        <>
          <Galleria foto={a.foto} />
          <div className="bg-white rounded-2xl p-4 space-y-2">
            <div className="text-[13px] text-green-800 font-semibold">{n(a.tipo)} · {n(a.immobile || a.settore)}</div>
            <h2 className="text-[20px] font-bold text-slate-900">{a.titolo}</h2>
            {a.categoria === 'immobili' && <div className="text-[22px] font-bold">{prezzo(a)}</div>}
            <div className="text-[14px] text-slate-600 flex items-center gap-1"><MapPin className="w-4 h-4" />{a.comune}{a.zona ? ` · ${a.zona}` : ''}</div>
            <div className="flex flex-wrap gap-1.5 text-[13px]">
              {a.mq && <span className="bg-slate-100 rounded-full px-2 py-0.5">{a.mq} m²</span>}
              {a.categoria === 'immobili' && <span className="bg-slate-100 rounded-full px-2 py-0.5">{tr(a.arredato ? 'Arredato' : 'Non arredato')}</span>}
              {a.spese_incluse && <span className="bg-slate-100 rounded-full px-2 py-0.5">{tr('Spese incluse')}</span>}
              {a.disponibile_dal && <span className="bg-slate-100 rounded-full px-2 py-0.5">{tr('Disponibile dal')} {dataIt(a.disponibile_dal)}</span>}
              {a.orario && <span className="bg-slate-100 rounded-full px-2 py-0.5">{n(a.orario)}</span>}
              {a.retribuzione && <span className="bg-slate-100 rounded-full px-2 py-0.5">{a.retribuzione}</span>}
            </div>
            <p className="text-[15px] text-slate-800 whitespace-pre-line">{a.descrizione}</p>
          </div>
          <Contatti c={a.contatti || {}} />
          <p className="text-[12px] text-slate-500 flex gap-1.5"><Info className="w-4 h-4 shrink-0" />{tr(AVVISO)}</p>
          <div className="flex justify-center gap-5">
            <button onClick={() => setSegnala(true)} className="flex items-center gap-1.5 text-red-700 text-[14px] py-2"><Flag className="w-4 h-4" />{tr('Segnala annuncio')}</button>
            <button onClick={() => { if (window.confirm(tr('Non vedrai più gli annunci di questo inserzionista su questo telefono. Continuare?'))) { nascondiInserzionista(a.inserzionista); onIndietro(); } }}
              className="flex items-center gap-1.5 text-slate-600 text-[14px] py-2"><X className="w-4 h-4" />{tr('Blocca inserzionista')}</button>
          </div>
          {segnala && <Segnala id={a.id} token={token} onChiudi={() => setSegnala(false)} />}
        </>
      )}
    </div>
  );
}

function Condizioni({ token, onAccettate, onChiudi }) {
  const [c, setC] = useState(null);
  useEffect(() => { chiama('/annunci/condizioni').then(setC).catch(() => {}); }, []);
  const accetta = async () => { await chiama('/annunci-io/condizioni', { metodo: 'POST', corpo: { versione: c.versione }, token }); onAccettate(); };
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center" onClick={onChiudi}>
      <div className="bg-white w-full max-w-xl max-h-[85vh] rounded-t-3xl sm:rounded-3xl p-5 flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
        <div className="text-[18px] font-bold">{tr('Condizioni d\'uso degli annunci')}</div>
        <div className="overflow-y-auto text-[14px] text-slate-700 whitespace-pre-line flex-1">{c ? c.testo : <Loader2 className="w-5 h-5 animate-spin" />}</div>
        <button disabled={!c} onClick={accetta} className="w-full rounded-xl bg-green-800 text-white font-semibold py-3">{tr('Ho letto e accetto')}</button>
      </div>
    </div>
  );
}

const VUOTO = { categoria: 'immobili', tipo: 'affitto', immobile: 'stanza', settore: 'confezione', orario: 'tempo_pieno', titolo: '',
  descrizione: '', comune: 'PRATO', zona: '', prezzo: '', mq: '', arredato: false, spese_incluse: false, disponibile_dal: '',
  retribuzione: '', contatti: { telefono: '', whatsapp: '', wechat: '' } };

function Campo({ etichetta, children }) {
  return <label className="block"><span className="text-[13px] font-medium text-slate-600">{etichetta}</span>{children}</label>;
}
const stileInput = 'mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[16px] bg-white';

function Modulo({ token, io: datiIo, annuncio, onFatto, onIndietro }) {
  const [d, setD] = useState(() => (annuncio ? { ...VUOTO, ...annuncio, prezzo: annuncio.prezzo ?? '', mq: annuncio.mq ?? '' } : { ...VUOTO }));
  const [persona, setPersona] = useState(annuncio?.client_id || datiIo.persone[0]?.id || '');
  const [tenute, setTenute] = useState(annuncio?.foto_nomi || []);
  const [nuove, setNuove] = useState([]);
  const [errore, setErrore] = useState('');
  const [invio, setInvio] = useState(false);
  const [condizioni, setCondizioni] = useState(false);
  const set = (k) => (e) => setD((x) => ({ ...x, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));
  const setC = (k) => (e) => setD((x) => ({ ...x, contatti: { ...x.contatti, [k]: e.target.value } }));
  const immobili = d.categoria === 'immobili';
  const totaleFoto = tenute.length + nuove.length;

  const aggiungi = async (files) => {
    setErrore('');
    const lista = Array.from(files || []).slice(0, 8 - totaleFoto);
    try { const ridotte = await Promise.all(lista.map(riduciFoto)); setNuove((x) => [...x, ...ridotte.map((f) => ({ f, url: URL.createObjectURL(f) }))]); } catch (_) { setErrore(tr('Formato foto non supportato')); }
  };
  const pubblica = async () => {
    if (!datiIo.condizioni_accettate && !annuncio) { setCondizioni(true); return; }
    setInvio(true); setErrore('');
    try {
      const dati = { ...d, client_id: persona, foto_tenute: tenute };
      const file = immobili ? nuove.map((x) => x.f) : [];
      if (annuncio) await chiamaModulo(`/annunci-io/${annuncio.id}`, { metodo: 'PUT', dati, file, token });
      else await chiamaModulo('/annunci-io', { dati, file, token });
      onFatto();
    } catch (e) { setErrore(e.message); setInvio(false); }
  };

  return (
    <div className="space-y-3">
      <button onClick={onIndietro} className="inline-flex items-center gap-1 rounded-full bg-white shadow px-3 py-1.5 text-[15px] text-green-800 font-medium"><ChevronLeft className="w-4 h-4" />{tr('I miei annunci')}</button>
      <div className="bg-white rounded-2xl p-4 space-y-3">
        <div className="text-[18px] font-bold">{tr(annuncio ? 'Modifica annuncio' : 'Nuovo annuncio')}</div>
        {!annuncio && (
          <div className="flex gap-2">
            <Scheda attiva={immobili} onClick={() => setD((x) => ({ ...x, categoria: 'immobili', tipo: 'affitto' }))}><Home className="w-4 h-4" />{tr('Immobili')}</Scheda>
            <Scheda attiva={!immobili} onClick={() => setD((x) => ({ ...x, categoria: 'lavoro', tipo: 'offro' }))}><Briefcase className="w-4 h-4" />{tr('Lavoro')}</Scheda>
          </div>
        )}
        {datiIo.persone.length > 1 && !annuncio && (
          <Campo etichetta={tr('Pubblica come')}>
            <select value={persona} onChange={(e) => setPersona(e.target.value)} className={stileInput}>
              {datiIo.persone.map((p) => <option key={p.id} value={p.id}>{p.nome} ({p.punti} {tr('punti')})</option>)}
            </select>
          </Campo>
        )}
        <div className="flex gap-2">{TIPI[d.categoria].map((t) => <Pillola key={t} attiva={d.tipo === t} onClick={() => setD((x) => ({ ...x, tipo: t }))}>{n(t)}</Pillola>)}</div>
        {immobili ? (
          <Campo etichetta={tr('Tipo di immobile')}><select value={d.immobile} onChange={set('immobile')} className={stileInput}>{IMMOBILI.map((t) => <option key={t} value={t}>{n(t)}</option>)}</select></Campo>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Campo etichetta={tr('Settore')}><select value={d.settore} onChange={set('settore')} className={stileInput}>{SETTORI.map((t) => <option key={t} value={t}>{n(t)}</option>)}</select></Campo>
            <Campo etichetta={tr('Orario')}><select value={d.orario} onChange={set('orario')} className={stileInput}>{ORARI.map((t) => <option key={t} value={t}>{n(t)}</option>)}</select></Campo>
          </div>
        )}
        <Campo etichetta={tr('Titolo')}><input maxLength={80} value={d.titolo} onChange={set('titolo')} className={stileInput} /></Campo>
        <Campo etichetta={tr('Descrizione')}><textarea rows={5} maxLength={3000} value={d.descrizione} onChange={set('descrizione')} className={stileInput} /></Campo>
        <div className="grid grid-cols-2 gap-2">
          <Campo etichetta={tr('Comune')}><input value={d.comune} onChange={set('comune')} className={stileInput} /></Campo>
          {immobili ? <Campo etichetta={tr('Zona (senza indirizzo)')}><input maxLength={80} value={d.zona} onChange={set('zona')} className={stileInput} /></Campo>
            : <Campo etichetta={tr('Retribuzione (facoltativa)')}><input maxLength={80} value={d.retribuzione} onChange={set('retribuzione')} className={stileInput} /></Campo>}
        </div>
        {immobili && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Campo etichetta={d.tipo === 'affitto' ? tr('Prezzo €/mese') : tr('Prezzo €')}><input inputMode="decimal" value={d.prezzo} onChange={set('prezzo')} className={stileInput} /></Campo>
              <Campo etichetta={tr('Metri quadri')}><input inputMode="numeric" value={d.mq} onChange={set('mq')} className={stileInput} /></Campo>
            </div>
            <Campo etichetta={tr('Disponibile dal')}><input type="date" value={d.disponibile_dal} onChange={set('disponibile_dal')} className={stileInput} /></Campo>
            <label className="flex items-center gap-2 text-[15px]"><input type="checkbox" checked={d.arredato} onChange={set('arredato')} />{tr('Arredato')}</label>
            <label className="flex items-center gap-2 text-[15px]"><input type="checkbox" checked={d.spese_incluse} onChange={set('spese_incluse')} />{tr('Spese incluse')}</label>
            <div>
              <div className="text-[13px] font-medium text-slate-600 mb-1">{tr('Foto')} ({totaleFoto}/8)</div>
              <div className="grid grid-cols-4 gap-2">
                {tenute.map((nome) => (
                  <div key={nome} className="relative">
                    <Foto src={`/api/area-clienti/annunci-io/${annuncio.id}/foto/${nome}`} token={token} className="w-full aspect-square object-cover rounded-lg" />
                    <button onClick={() => setTenute((x) => x.filter((y) => y !== nome))} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                {nuove.map((x, i) => (
                  <div key={x.url} className="relative">
                    <img src={x.url} alt="" className="w-full aspect-square object-cover rounded-lg" />
                    <button onClick={() => setNuove((l) => l.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                {totaleFoto < 8 && (
                  <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 text-[12px] cursor-pointer">
                    <Camera className="w-6 h-6" />{tr('Aggiungi')}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { aggiungi(e.target.files); e.target.value = ''; }} />
                  </label>
                )}
              </div>
              <p className="text-[12px] text-slate-500 mt-1">{tr('Solo foto della casa: niente persone, documenti o targhe.')}</p>
            </div>
          </>
        )}
        <div className="text-[15px] font-semibold pt-1">{tr('Contatti')}</div>
        <div className="grid gap-2">
          <Campo etichetta={tr('Telefono')}><input inputMode="tel" value={d.contatti.telefono} onChange={setC('telefono')} className={stileInput} /></Campo>
          <Campo etichetta="WhatsApp"><input inputMode="tel" value={d.contatti.whatsapp} onChange={setC('whatsapp')} className={stileInput} /></Campo>
          <Campo etichetta={tr('ID WeChat')}><input value={d.contatti.wechat} onChange={setC('wechat')} className={stileInput} /></Campo>
        </div>
        <p className="text-[13px] text-amber-800 bg-amber-50 rounded-xl p-2.5">{tr('I contatti saranno visibili a tutti, anche a chi non è cliente.')}</p>
        {errore && <div className="text-red-700 text-[14px]">{errore}</div>}
        <button disabled={invio} onClick={pubblica} className="w-full rounded-xl bg-green-800 disabled:opacity-60 text-white font-semibold py-3 flex items-center justify-center gap-2">
          {invio && <Loader2 className="w-5 h-5 animate-spin" />}{tr(annuncio ? 'Salva e ripubblica' : 'Pubblica')}
        </button>
        <p className="text-[12px] text-slate-500">{tr('Prima di andare online l\'annuncio viene controllato in automatico (di solito meno di un minuto).')}</p>
      </div>
      {condizioni && <Condizioni token={token} onChiudi={() => setCondizioni(false)}
        onAccettate={() => { setCondizioni(false); datiIo.condizioni_accettate = true; pubblica(); }} />}
    </div>
  );
}

// "I miei annunci" divisi per stato: prima quelli su cui il cliente deve fare qualcosa
const GRUPPI = [
  ['Da correggere', ['bloccato'], 'Non pubblicati: leggi il motivo, correggi e ripubblica'],
  ['Online', ['online'], ''],
  ['In attesa', ['in_verifica', 'da_guardare', 'nascosto'], 'In controllo automatico o dallo studio'],
  ['In pausa', ['pausa', 'pausa_punti'], ''],
  ['Tolti dallo studio', ['tolto'], ''],
];

function IMiei({ token, onApriModulo }) {
  const [io, setIo] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [gruppo, setGruppo] = useState('');
  const [errore, setErrore] = useState('');
  const [richiesta, setRichiesta] = useState('');
  const carica = useCallback(() => chiama('/annunci-io', { token }).then(setIo).catch((e) => setErrore(e.message)), [token]);
  useEffect(() => { carica(); }, [carica]);
  useEffect(() => {   // annunci in verifica: si aggiorna da solo
    if (!io?.annunci.some((a) => a.stato === 'in_verifica')) return undefined;
    const t = setInterval(carica, 5000);
    return () => clearInterval(t);
  }, [io, carica]);
  const azione = async (a, percorso, metodo = 'POST') => {
    if (metodo === 'DELETE' && !window.confirm(tr('Eliminare l\'annuncio?'))) return;
    try { await chiama(`/annunci-io/${a.id}${percorso}`, { metodo, token }); } catch (e) { setErrore(e.message); }
    carica();
  };
  const chiedi = async () => { setRichiesta('invio'); try { await chiama('/annunci-io/richiesta', { metodo: 'POST', token }); setRichiesta(''); carica(); } catch (e) { setRichiesta(e.message); } };

  if (!io) return <div className="flex justify-center py-8">{errore || <Loader2 className="w-6 h-6 animate-spin text-green-800" />}</div>;
  const puoPubblicare = io.persone.length > 0 && io.punti > 0 && !io.bloccato && io.pubblica_qui;
  const gestisce = !io.bloccato && io.pubblica_qui;   // modifica / riattiva
  return (
    <div className="space-y-3">
      <div className="bg-white rounded-2xl p-4 flex items-center gap-3">
        <div className="flex-1">
          <div className="text-[13px] text-slate-500">{tr('I tuoi punti')}</div>
          <div className="text-[26px] font-bold text-green-800">{io.punti}</div>
          <div className="text-[12px] text-slate-500">{tr('1 punto = 1 annuncio online per 7 giorni, rinnovo automatico')}</div>
        </div>
        {puoPubblicare && <button onClick={() => onApriModulo(io, null)} className="rounded-xl bg-green-800 text-white font-semibold px-4 py-3 flex items-center gap-1.5"><Plus className="w-5 h-5" />{tr('Nuovo')}</button>}
      </div>
      {io.bloccato && <p className="rounded-2xl bg-red-50 text-red-800 text-[14px] p-4">{tr('Non puoi più pubblicare annunci: contatta lo studio')}</p>}
      {!io.bloccato && !io.pubblica_qui && (
        <p className="rounded-2xl bg-sky-50 text-sky-900 text-[14px] p-4">{tr('Per pubblicare o modificare i tuoi annunci usa il sito clienti.crm-jv.com dal browser.')}</p>
      )}
      {!puoPubblicare && !io.bloccato && io.pubblica_qui && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 space-y-2">
          <div className="text-[15px] font-semibold text-green-900">{tr('Vuoi pubblicare un annuncio?')}</div>
          {io.richiesta_inviata
            ? <p className="text-[14px] text-green-900">{tr('Richiesta inviata: lo studio ti contatterà.')}</p>
            : (
              <>
                <p className="text-[14px] text-green-900">{tr('Chiedi allo studio di attivare i punti per gli annunci.')}</p>
                <button disabled={richiesta === 'invio'} onClick={chiedi} className="rounded-xl bg-green-800 text-white font-semibold px-4 py-2.5">{tr('Chiedi allo studio')}</button>
                {richiesta && richiesta !== 'invio' && <div className="text-red-700 text-[14px]">{richiesta}</div>}
              </>
            )}
        </div>
      )}
      {errore && <div className="text-red-700 text-[14px]">{errore}</div>}
      {io.annunci.length > 0 && (
        <div className="flex gap-2">
          {[['', 'Tutti'], ['immobili', 'Immobili'], ['lavoro', 'Lavoro']].map(([k, t]) => (
            <Pillola key={k || 'tutti'} attiva={filtro === k} onClick={() => { setFiltro(k); setGruppo(''); }}>
              {tr(t)} ({io.annunci.filter((a) => !k || a.categoria === k).length})
            </Pillola>
          ))}
        </div>
      )}
      {io.annunci.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          <Pillola attiva={!gruppo} onClick={() => setGruppo('')}>{tr('Tutti gli stati')}</Pillola>
          {GRUPPI.map(([titolo, stati]) => {
            const n = io.annunci.filter((a) => stati.includes(a.stato) && (!filtro || a.categoria === filtro)).length;
            return n > 0 && <Pillola key={titolo} attiva={gruppo === titolo} onClick={() => setGruppo(gruppo === titolo ? '' : titolo)}>{tr(titolo)} ({n})</Pillola>;
          })}
        </div>
      )}
      {GRUPPI.filter(([titolo]) => !gruppo || gruppo === titolo).map(([titolo, stati, nota]) => {
        const elenco = io.annunci.filter((a) => stati.includes(a.stato) && (!filtro || a.categoria === filtro));
        if (!elenco.length) return null;
        return (
          <section key={titolo} className="space-y-2">
            <div className="px-1 pt-1">
              <div className="text-[14px] font-bold text-slate-700">{tr(titolo)} · {elenco.length}</div>
              {nota && <div className="text-[12px] text-slate-500">{tr(nota)}</div>}
            </div>
            {elenco.map((a) => (
              <div key={a.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <Carta a={a} token={token} stato piatta onApri={() => {}} />
                {['bloccato', 'tolto'].includes(a.stato) && tz(a, 'motivo') && (
                  <p className="mx-3 mb-2 rounded-xl bg-red-50 text-red-800 text-[13px] p-2.5">{tz(a, 'motivo')}</p>
                )}
                <div className="flex items-center gap-4 border-t border-slate-100 px-3 py-2">
                  {a.stato === 'online' && a.online_fino && <span className="text-[12px] text-slate-500">{tr('Rinnovo il')} {dataIt(a.online_fino)}</span>}
                  {gestisce && !['tolto', 'nascosto'].includes(a.stato) && <button onClick={() => onApriModulo(io, a)} className="flex items-center gap-1 text-[13px] text-slate-700"><Pencil className="w-4 h-4" />{tr(a.stato === 'bloccato' ? 'Correggi' : 'Modifica')}</button>}
                  {a.stato === 'online' && <button onClick={() => azione(a, '/pausa')} className="flex items-center gap-1 text-[13px] text-slate-700"><Pause className="w-4 h-4" />{tr('Pausa')}</button>}
                  {gestisce && ['pausa', 'pausa_punti'].includes(a.stato) && <button onClick={() => azione(a, '/riattiva')} className="flex items-center gap-1 text-[13px] text-green-800"><Play className="w-4 h-4" />{tr('Riattiva')}</button>}
                  <button onClick={() => azione(a, '', 'DELETE')} className="flex items-center gap-1 text-[13px] text-red-700 ml-auto"><Trash2 className="w-4 h-4" />{tr('Elimina')}</button>
                </div>
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}

export default function Annunci({ token, onAccedi }) {
  const [categoria, setCategoria] = useState('immobili');
  const [vista, setVista] = useState({ tipo: 'elenco' });
  const vai = (v) => { setVista(v); window.scrollTo({ top: 0 }); };

  if (vista.tipo === 'dettaglio') return <Dettaglio id={vista.id} token={token} onIndietro={() => vai({ tipo: 'elenco' })} />;
  if (vista.tipo === 'modulo') return <Modulo token={token} io={vista.io} annuncio={vista.annuncio} onIndietro={() => vai({ tipo: 'miei' })} onFatto={() => vai({ tipo: 'miei' })} />;
  return (
    <div className="space-y-3">
      <div className="flex gap-2 bg-slate-100 p-1 rounded-2xl">
        <Scheda attiva={vista.tipo === 'elenco' && categoria === 'immobili'} onClick={() => { setCategoria('immobili'); vai({ tipo: 'elenco' }); }}><Home className="w-4 h-4" />{tr('Immobili')}</Scheda>
        <Scheda attiva={vista.tipo === 'elenco' && categoria === 'lavoro'} onClick={() => { setCategoria('lavoro'); vai({ tipo: 'elenco' }); }}><Briefcase className="w-4 h-4" />{tr('Lavoro')}</Scheda>
        <Scheda attiva={vista.tipo === 'miei'} onClick={() => (token ? vai({ tipo: 'miei' }) : onAccedi())}>{tr('I miei')}</Scheda>
      </div>
      {vista.tipo === 'miei'
        ? <IMiei token={token} onApriModulo={(io, annuncio) => vai({ tipo: 'modulo', io, annuncio })} />
        : <Elenco categoria={categoria} onApri={(id) => vai({ tipo: 'dettaglio', id })} />}
    </div>
  );
}
